import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/auth";
import { APP_VERSION, APP_BUILD_DATE } from "@/lib/version";
import { api } from "@/lib/api";
import { StatusBanner } from "@/components/StatusBanner";
import { LogOut, Menu, X } from "lucide-react";

export const AppShell = ({ nav, sections, brand, children, testIdPrefix = "shell" }) => {
  const { user, tenant, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);

  useEffect(() => {
    api.get("/version").then(({ data }) => setBackendVersion(data.backend)).catch(() => {});
  }, []);

  const sidebar = (
    <aside data-testid={`${testIdPrefix}-sidebar`}
           className="sidebar-nav w-60 h-full flex flex-col shrink-0">
      <div className="px-4 py-4 border-b border-white/10 flex items-center gap-3 shrink-0">
        <div className="w-8 h-8 bg-blue-600 grid place-items-center rounded">
          <span className="font-black text-white">T</span>
        </div>
        <div className="min-w-0">
          <div className="font-black text-white text-sm truncate">{brand.title}</div>
          <div className="label-overline text-zinc-500">{brand.subtitle}</div>
        </div>
        <button className="ml-auto lg:hidden text-zinc-300"
                data-testid="shell-close-mobile"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu">
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 min-h-0">
        {sections.map((sec, i) => (
          <div key={i}>
            {sec.label && <div className="section">{sec.label}</div>}
            {sec.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => isActive ? "active" : ""}
              >
                {item.icon}
                <span className="truncate">{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className="ml-auto bg-blue-600 text-white text-[10px] px-1.5 py-0.5 font-bold rounded">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3 shrink-0">
        <div className="text-xs text-zinc-400 mb-1 font-mono truncate" data-testid="shell-current-user">
          {user?.email}
        </div>
        <div className="label-overline text-zinc-500 mb-3 truncate">
          {ROLE_LABELS[user?.role]}
        </div>
        <button
          data-testid="shell-logout"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 text-xs text-zinc-300 hover:text-white
                     border border-zinc-700 hover:border-zinc-500 rounded py-2 transition"
        >
          <LogOut size={14} /> Sign out
        </button>
        <div className="mt-3 text-[10px] leading-tight font-mono text-zinc-500 truncate"
             data-testid="shell-version-footer"
             title={`Frontend ${APP_VERSION} (${APP_BUILD_DATE})${backendVersion ? ` · Backend ${backendVersion}` : ""}`}>
          v{APP_VERSION} · {APP_BUILD_DATE}
          {backendVersion && backendVersion !== APP_VERSION && (
            <span className="text-amber-400"> · api {backendVersion}</span>
          )}
        </div>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:block h-full">{sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40"
               data-testid="shell-mobile-backdrop"
               onClick={() => setMobileOpen(false)} />
          <div className="lg:hidden fixed top-0 left-0 h-full z-50">{sidebar}</div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Mobile top-bar */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 shrink-0">
          <button data-testid="shell-open-mobile"
                  onClick={() => setMobileOpen(true)}
                  className="text-slate-800"
                  aria-label="Open menu">
            <Menu size={22} />
          </button>
          <div className="font-black text-slate-900 text-sm">{brand.title}</div>
        </div>

        <StatusBanner tenant={tenant} />

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
};
